import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Research Jobs Store
 * ===================
 *
 * Tracks background research jobs kicked off by the case wizard's "smart"
 * endpoints (industry research, competition analysis). Persisted to
 * localStorage so the user can leave the wizard and come back later — the
 * <ResearchJobsNotifier /> component polls active jobs and surfaces a toast
 * when they finish.
 */

export type ResearchJobKind = 'industry_research' | 'competition_analysis';
export type ResearchJobStatus = 'running' | 'completed' | 'failed';

export interface ResearchJob {
  jobId: string;
  kind: ResearchJobKind;
  status: ResearchJobStatus;
  progress: number;          // 0-100
  message: string;           // current step description from backend
  label: string;             // user-facing label e.g. "Industry research for Apple"
  companyName?: string;
  industryName?: string;
  result?: any;              // populated when status === 'completed'
  error?: string;            // populated when status === 'failed'
  createdAt: number;         // ms epoch
  updatedAt: number;         // ms epoch
  // Where to send the user when they click "Resume" on the completion toast.
  // For wizard-initiated jobs this is '/cases/new-wizard'.
  resumePath?: string;
  // True once we've shown the completion/failure toast to the user.
  notified?: boolean;
  // True once the user has acknowledged a failure (e.g. by retrying), so the
  // in-page error screen for it can be suppressed. Distinct from `notified`,
  // which only tracks whether the toast has fired.
  dismissed?: boolean;
}

interface ResearchJobsState {
  jobs: Record<string, ResearchJob>;

  // Register a new job (called immediately after backend returns jobId)
  registerJob: (job: Omit<ResearchJob, 'createdAt' | 'updatedAt' | 'status' | 'progress' | 'message'> & {
    status?: ResearchJobStatus;
    progress?: number;
    message?: string;
  }) => void;

  // Update a job from a poll response
  updateJob: (jobId: string, patch: Partial<ResearchJob>) => void;

  // Mark a job as notified so the toast isn't shown twice
  markNotified: (jobId: string) => void;

  // Drop a job (e.g. after user resumes the wizard or dismisses)
  removeJob: (jobId: string) => void;

  // Drop all finished+notified jobs older than maxAgeMs (default 1 hour)
  pruneOldJobs: (maxAgeMs?: number) => void;

  // Quick selector: jobs that still need polling
  getActiveJobs: () => ResearchJob[];
}

const PRUNE_DEFAULT_MS = 60 * 60 * 1000; // 1 hour

export const useResearchJobsStore = create<ResearchJobsState>()(
  persist(
    (set, get) => ({
      jobs: {},

      registerJob: (job) => set((state) => {
        const now = Date.now();
        return {
          jobs: {
            ...state.jobs,
            [job.jobId]: {
              ...job,
              status: job.status ?? 'running',
              progress: job.progress ?? 0,
              message: job.message ?? 'Starting...',
              createdAt: now,
              updatedAt: now,
              notified: false,
            },
          },
        };
      }),

      updateJob: (jobId, patch) => set((state) => {
        const existing = state.jobs[jobId];
        if (!existing) return state;
        return {
          jobs: {
            ...state.jobs,
            [jobId]: {
              ...existing,
              ...patch,
              updatedAt: Date.now(),
            },
          },
        };
      }),

      markNotified: (jobId) => set((state) => {
        const existing = state.jobs[jobId];
        if (!existing) return state;
        return {
          jobs: {
            ...state.jobs,
            [jobId]: { ...existing, notified: true },
          },
        };
      }),

      removeJob: (jobId) => set((state) => {
        const { [jobId]: _drop, ...rest } = state.jobs;
        return { jobs: rest };
      }),

      pruneOldJobs: (maxAgeMs = PRUNE_DEFAULT_MS) => set((state) => {
        const now = Date.now();
        const next: Record<string, ResearchJob> = {};
        for (const [jid, job] of Object.entries(state.jobs)) {
          const finished = job.status === 'completed' || job.status === 'failed';
          const stale = finished && job.notified && (now - job.updatedAt) > maxAgeMs;
          if (!stale) next[jid] = job;
        }
        return { jobs: next };
      }),

      getActiveJobs: () => {
        return Object.values(get().jobs).filter(j => j.status === 'running');
      },
    }),
    {
      name: 'case-wizard-research-jobs',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
